/**
 * Review Service - Manages review workflows for deliverables
 * Handles review CRUD, items, comments, and file uploads
 */

import { db, auth, functions } from '@/js/firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  writeBatch,
  runTransaction,
  deleteField
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { logger } from './loggerService';
import { toastBridge } from './toastBridge';
import { detectAssetType, getMediaMetadata } from './assetUploadService';
import { uploadAsset as uploadToBunny } from './bunnyUploadService';
import { BUNNY_UPLOAD_CONTEXT } from '@/utils/bunnyConstants';
import { deleteReviewSubcollections } from './firestoreUtils';
import { deliverableService } from './deliverableService';
import { REVIEW_ITEM_STATUS, FINALIZATION_STATUS, isActionableStatus, computeSlotChangeFlags } from '@/utils/reviewUtils';

const stripExtension = (fileName) => String(fileName).replace(/\.[^/.]+$/, '');

class ReviewService {
  constructor() {
    this.listeners = new Map();
    this._heartbeatIntervals = new Map();
  }

  // ─── Queries ─────────────────────────────────────────────────

  /**
   * Find the active review for a company.
   * Returns the most recent review, or null.
   */
  async getActiveReviewForCompany(companyId) {
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef,
        where('companyId', '==', companyId)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      // Return the most recently created review
      const reviews = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      reviews.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return bTime - aTime;
      });
      return reviews[0];
    } catch (error) {
      logger.error('[ReviewService] Failed to find active review', { error: error.message, companyId });
      return null;
    }
  }

  /**
   * Find ALL active reviews for a company.
   * Returns sorted array (newest first), or empty array.
   */
  async getActiveReviewsForCompany(companyId) {
    try {
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef,
        where('companyId', '==', companyId)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return [];
      const reviews = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      reviews.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return bTime - aTime;
      });
      return reviews;
    } catch (_error) {
      logger.error('[ReviewService] Failed to find active reviews', { error: _error.message, companyId });
      return [];
    }
  }

  // ─── Review CRUD ───────────────────────────────────────────

  /**
   * Create a new review (reviewId === deliverableId)
   */
  async createReview(deliverableId, projectId, companyId) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const userName = await this._getUserName(user);
      const reviewRef = doc(db, 'reviews', deliverableId);

      const reviewData = {
        deliverableId,
        projectId,
        companyId,
        status: 'draft',
        itemCount: 0,
        approvedCount: 0,
        changesRequestedCount: 0,
        pendingCount: 0,
        commentCount: 0,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        createdByName: userName,
        updatedAt: serverTimestamp(),
        updatedBy: null
      };

      await setDoc(reviewRef, reviewData);

      deliverableService.logActivity(deliverableId, 'review_created', null, null, null, 'Review created').catch(() => {});

      logger.info('[ReviewService] Review created', { deliverableId, projectId, companyId });
      return { id: deliverableId, ...reviewData };
    } catch (error) {
      logger.error('[ReviewService] Failed to create review', { error: error.message, deliverableId });
      toastBridge.error('Failed to create review');
      throw error;
    }
  }

  /**
   * Get a single review document
   */
  async getReview(reviewId) {
    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      const snapshot = await getDoc(reviewRef);
      if (!snapshot.exists()) return null;
      return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
      if (error.code === 'permission-denied') return null;
      logger.error('[ReviewService] Failed to get review', { error: error.message, reviewId });
      return null;
    }
  }

  /**
   * Subscribe to real-time review doc updates
   */
  subscribeToReview(reviewId, callback, { key = `review_${reviewId}`, onError } = {}) {
    this.unsubscribe(key);
    let retries = 0;

    const subscribe = () => {
      const reviewRef = doc(db, 'reviews', reviewId);
      const unsub = onSnapshot(reviewRef, (snapshot) => {
        retries = 0; // Reset on success
        if (snapshot.exists()) {
          callback({ id: snapshot.id, ...snapshot.data() });
        } else {
          callback(null);
        }
      }, (error) => {
        logger.firestoreListenerError('[ReviewService] Review subscription error', error, { reviewId });
        if (retries < 3) {
          retries++;
          const delay = 1000 * retries;
          logger.info('[ReviewService] Retrying review subscription', { reviewId, retry: retries });
          setTimeout(() => { if (this.listeners.has(key)) subscribe(); }, delay);
        } else if (onError) {
          onError(error);
        }
      });
      this.listeners.set(key, unsub);
      return unsub;
    };

    return subscribe();
  }

  /**
   * Update review fields
   */
  async updateReview(reviewId, updates) {
    try {
      const user = auth.currentUser;
      const reviewRef = doc(db, 'reviews', reviewId);
      await updateDoc(reviewRef, {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null
      });
      logger.info('[ReviewService] Review updated', { reviewId });
    } catch (error) {
      logger.error('[ReviewService] Failed to update review', { error: error.message, reviewId });
      toastBridge.error('Failed to update review');
      throw error;
    }
  }

  // ─── Items ─────────────────────────────────────────────────

  /**
   * Add a new item with file upload (creates v1)
   */
  async addItem(reviewId, itemData, file, companyId, onProgress, onTask) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const userName = await this._getUserName(user);
      const itemRef = doc(collection(db, 'reviews', reviewId, 'items'));
      const itemId = itemRef.id;
      const versionId = `v_${Date.now()}`;

      // Extract media metadata (aspect ratio, duration) before upload
      const metadata = file ? await getMediaMetadata(file) : { aspectRatio: null, duration: null };

      // Upload file to Bunny (Stream for video, Storage otherwise).
      // Returns { bunnyStream?: {...} } | { bunnyStorage?: {...} } depending
      // on file type.
      let uploadResult = {};
      if (file) {
        uploadResult = await this.uploadReviewFile(companyId, reviewId, itemId, versionId, file, onProgress, onTask);
      }

      const version = {
        id: versionId,
        versionNumber: 1,
        label: 'v1',
        clientVersionNumber: null,
        fileName: file?.name || itemData.name,
        fileSize: file?.size || 0,
        ...uploadResult,
        duration: metadata.duration || itemData.duration || null,
        aspectRatio: metadata.aspectRatio || null,
        width: metadata.width || itemData.width || null,
        height: metadata.height || itemData.height || null,
        thumbnailUrl: null,
        thumbnailStoragePath: null,
        createdAt: new Date(),
        createdBy: user.uid,
        createdByName: userName,
        note: null
      };

      const itemDoc = {
        reviewId,
        name: itemData.name || file?.name || 'Untitled',
        description: itemData.description || null,
        sortOrder: itemData.sortOrder ?? 0,
        slot: itemData.slot || null, // podcast review slot key; null for free-form items
        type: itemData.type || this._reviewFileType(file),
        mimeType: file?.type || itemData.mimeType || 'application/octet-stream',
        currentVersionId: versionId,
        currentVersionNumber: 1,
        versions: [version],
        internalStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
        clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
        internalApprovedBy: null,
        internalApprovedAt: null,
        clientApprovedBy: null,
        clientApprovedAt: null,
        requiresFinalization: itemData.requiresFinalization || false,
        finalizationNote: itemData.finalizationNote || null,
        finalizationStatus: null,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
        updatedBy: null
      };

      await setDoc(itemRef, itemDoc);

      // Reset deliverable review state — a new unreviewed item invalidates
      // any previous aggregate review outcome (fire-and-forget)
      this._resetDeliverableReviewState(reviewId, `New item "${itemDoc.name}" added`).catch(() => {});

      deliverableService.logActivity(
        reviewId,
        'review_item_added',
        null,
        null,
        itemDoc.name,
        `Added "${itemDoc.name}" to review`
      ).catch(() => {});

      logger.info('[ReviewService] Item added', { reviewId, itemId });
      return { id: itemId, ...itemDoc };
    } catch (error) {
      // Downgraded to warn: this re-throws to the orchestration layer (queue/component),
      // which emits the single authoritative error log. Avoids triple-logging one failure.
      logger.warn('[ReviewService] Failed to add item', { error: error.message, reviewId });
      toastBridge.error('Failed to add review item');
      throw error;
    }
  }

  /**
   * Add a new version to an existing item
   */
  async addVersion(reviewId, itemId, file, companyId, note, onProgress, onTask) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const userName = await this._getUserName(user);
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);
      const itemSnapshot = await getDoc(itemRef);
      if (!itemSnapshot.exists()) throw new Error('Item not found');

      const item = itemSnapshot.data();
      const newVersionNumber = item.currentVersionNumber + 1;
      const versionId = `v_${Date.now()}`;

      // Extract media metadata (aspect ratio, duration) before upload
      const metadata = file ? await getMediaMetadata(file) : { aspectRatio: null, duration: null };

      // Upload file to Bunny — { bunnyStream?: {...} } | { bunnyStorage?: {...} }
      let uploadResult = {};
      if (file) {
        uploadResult = await this.uploadReviewFile(companyId, reviewId, itemId, versionId, file, onProgress, onTask);
      }

      const newVersion = {
        id: versionId,
        versionNumber: newVersionNumber,
        label: `v${newVersionNumber}`,
        clientVersionNumber: null,
        fileName: file?.name || item.name,
        fileSize: file?.size || 0,
        ...uploadResult,
        duration: metadata.duration || null,
        aspectRatio: metadata.aspectRatio || null,
        width: metadata.width || null,
        height: metadata.height || null,
        thumbnailUrl: null,
        thumbnailStoragePath: null,
        createdAt: new Date(),
        createdBy: user.uid,
        createdByName: userName,
        note: note || null
      };

      // Re-read the item inside a transaction and prepend the new version to
      // the FRESH versions array. The getDoc above happened before the upload
      // (which can take minutes for a large video); a Bunny transcode-ready
      // webhook that updated a SIBLING version's bunnyStream during that window
      // would be clobbered by a stale whole-array write. Reading fresh inside
      // the transaction preserves those concurrent updates.
      let isFinalizationCompletion = false;
      let reopenAction = null;
      await runTransaction(db, async (tx) => {
        const freshSnap = await tx.get(itemRef);
        if (!freshSnap.exists()) throw new Error('Item not found');
        const fresh = freshSnap.data();

        // Prepend new version (newest first) onto the fresh array.
        const versions = [newVersion, ...(fresh.versions || [])];

        const versionUpdate = {
          versions,
          currentVersionId: versionId,
          currentVersionNumber: newVersionNumber,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        };

        // Slot items are named after their file (never admin-authored), so the
        // name follows the newest upload — promotion + the approve modal read it.
        if (fresh.slot && file?.name) versionUpdate.name = stripExtension(file.name);

        // If internal changes were requested, transition to in_revision.
        // Note: clientStatus is intentionally NOT transitioned here — the item
        // must pass internal review again before being resubmitted to the client.
        reopenAction = null;
        if (fresh.internalStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
          reopenAction = 'review_internal_reopened';
          versionUpdate.internalStatus = REVIEW_ITEM_STATUS.IN_REVISION;
          versionUpdate.internalApprovedBy = null;
          versionUpdate.internalApprovedAt = null;
        }

        // Replacing a version the client can already see: the new one must be
        // re-approved internally, or promoteVersionsTrigger ships it to the
        // client immediately (it promotes any internally approved item).
        const freshCurrent = (fresh.versions || []).find(v => v.id === fresh.currentVersionId);
        if (fresh.internalStatus === REVIEW_ITEM_STATUS.APPROVED && freshCurrent?.clientVersionNumber != null) {
          reopenAction = 'review_client_unapproved';
          versionUpdate.internalStatus = REVIEW_ITEM_STATUS.PENDING_REVIEW;
          versionUpdate.internalApprovedBy = null;
          versionUpdate.internalApprovedAt = null;
        }

        // Uploading a new version while the item is pending finalization
        // counts as the internal team completing finalization work.
        isFinalizationCompletion = fresh.finalizationStatus === FINALIZATION_STATUS.PENDING;
        if (isFinalizationCompletion) {
          reopenAction = null;
          versionUpdate.internalStatus = REVIEW_ITEM_STATUS.APPROVED;
          versionUpdate.internalApprovedBy = user.uid;
          versionUpdate.internalApprovedAt = serverTimestamp();
        }

        tx.update(itemRef, versionUpdate);
      });

      deliverableService.logActivity(
        reviewId,
        'review_version_added',
        null,
        null,
        `v${newVersionNumber}`,
        `Added v${newVersionNumber} to "${item.name}"`
      ).catch(() => {});

      if (reopenAction) {
        const isInternalReopen = reopenAction === 'review_internal_reopened';
        deliverableService.logActivity(
          reviewId,
          reopenAction,
          'internalStatus',
          isInternalReopen ? REVIEW_ITEM_STATUS.CHANGES_REQUESTED : REVIEW_ITEM_STATUS.APPROVED,
          isInternalReopen ? REVIEW_ITEM_STATUS.IN_REVISION : REVIEW_ITEM_STATUS.PENDING_REVIEW,
          isInternalReopen
            ? `"${item.name}" moved to in revision with v${newVersionNumber}`
            : `"${item.name}" needs internal re-approval — v${newVersionNumber} replaces a client-visible version`
        ).catch(() => {});
      }

      if (isFinalizationCompletion) {
        await this.promoteVersionToClient(reviewId, itemId);
        await updateDoc(itemRef, {
          finalizationStatus: FINALIZATION_STATUS.COMPLETE,
          clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          clientApprovedBy: null,
          clientApprovedAt: null,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        });
        logger.info('[ReviewService] Finalization completed on version upload', { reviewId, itemId });
        this._syncFinalizationToDeliverable(reviewId, user.uid).catch((err) => {
          logger.warn('[ReviewService] Finalization sync failed', { reviewId, error: err.message });
        });
        deliverableService.logActivity(
          reviewId,
          'review_finalization_completed',
          null,
          null,
          `v${newVersionNumber}`,
          `Finalization completed for "${item.name}" with v${newVersionNumber}`
        ).catch(() => {});
      }

      logger.info('[ReviewService] Version added', { reviewId, itemId, versionNumber: newVersionNumber });
      return newVersion;
    } catch (error) {
      // 'Item not found' means the item was removed out from under the selection —
      // activeItemId can outlive its item after back-to-back removals. Same case
      // replaceVersionFile already handles; tell the user to re-pick.
      if (error.message === 'Item not found') {
        logger.warn('[ReviewService] Version target no longer exists', { reviewId, itemId });
        toastBridge.error('That item was removed. Select another item to upload a new version.');
        throw error;
      }
      // Downgraded to warn: re-thrown to the component, which emits the authoritative
      // error log. Keeps one log per failure instead of cascading service+component logs.
      logger.warn('[ReviewService] Failed to add version', { error: error.message, reviewId, itemId });
      toastBridge.error('Failed to add new version');
      throw error;
    }
  }

  /**
   * Replace the file on an existing version without creating a new version.
   * Useful for fixing upload mistakes without polluting version history.
   */
  async replaceVersionFile(reviewId, itemId, versionId, file, companyId, onProgress, onTask) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);
      const itemSnapshot = await getDoc(itemRef);
      if (!itemSnapshot.exists()) throw new Error('Item not found');

      const item = itemSnapshot.data();
      const versionIndex = item.versions.findIndex(v => v.id === versionId);
      if (versionIndex === -1) throw new Error('Version not found');

      const oldVersion = item.versions[versionIndex];

      // Extract media metadata from the new file
      const metadata = await getMediaMetadata(file);

      // Upload the new file FIRST, under the same version ID (Bunny). Returns
      // { bunnyStream?: {...} } | { bunnyStorage?: {...} }. If this throws, the
      // old version is still fully intact — we have not touched it yet.
      // Storage paths are `${versionId}_${filename}`, so a same-named replacement
      // would land on the old path — the cleanup below would then delete the new
      // file, and the unchanged path keeps the stale CDN/media cache. Stamp the
      // upload name so the path is always new (fileName below keeps the real name).
      const uploadFile = file.type?.startsWith('video/')
        ? file
        : new File([file], `${Date.now()}_${file.name}`, { type: file.type });
      const uploadResult = await this.uploadReviewFile(companyId, reviewId, itemId, versionId, uploadFile, onProgress, onTask);

      // Update the version in-place inside a transaction. The getDoc above
      // ran before the (possibly multi-minute) upload, so re-read the fresh
      // versions array here — otherwise a Bunny transcode webhook that touched
      // a SIBLING version's bunnyStream during the upload would be clobbered.
      // Locate by id (not the stale index) and strip legacy Firebase pointers
      // (storagePath/downloadUrl/thumbnail*) plus any prior Bunny pointers —
      // the new uploadResult is now the source of truth for this version.
      let updatedVersion = null;
      await runTransaction(db, async (tx) => {
        const freshSnap = await tx.get(itemRef);
        if (!freshSnap.exists()) throw new Error('Item not found');
        const versions = [...(freshSnap.data().versions || [])];
        const idx = versions.findIndex(v => v.id === versionId);
        if (idx === -1) throw new Error('Version not found');
        const { storagePath: _sp, downloadUrl: _du, thumbnailStoragePath: _tsp, thumbnailUrl: _tu,
                bunnyStream: _bs, bunnyStorage: _bst, ...rest } = versions[idx];
        updatedVersion = {
          ...rest,
          fileName: file.name,
          fileSize: file.size,
          ...uploadResult,
          duration: metadata.duration || null,
          aspectRatio: metadata.aspectRatio || null,
          width: metadata.width || null,
          height: metadata.height || null,
          thumbnailUrl: null,
          thumbnailStoragePath: null,
          replacedAt: new Date(),
          replacedBy: user.uid,
        };
        versions[idx] = updatedVersion;
        const replaceUpdate = {
          versions,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
        };
        if (freshSnap.data().slot && freshSnap.data().currentVersionId === versionId) {
          replaceUpdate.name = stripExtension(file.name);
        }
        tx.update(itemRef, replaceUpdate);
      });

      // Only after the new file is uploaded AND the doc points at it do we
      // remove the old artifacts. Best-effort: the version no longer references
      // them, so a cleanup failure leaves orphaned bytes (logged) but never
      // loses the live file. Legacy versions sit on Firebase Storage; Bunny
      // versions need their Bunny artifacts removed.
      try {
        await this._deleteVersionArtifacts(oldVersion, companyId);
      } catch (cleanupErr) {
        logger.warn('[ReviewService] Old version artifact cleanup failed (non-fatal)', {
          reviewId, itemId, versionId, error: cleanupErr.message,
        });
      }

      const versionLabel = oldVersion.label || `v${oldVersion.versionNumber}`;
      deliverableService.logActivity(
        reviewId,
        'review_version_replaced',
        null,
        oldVersion.fileName || null,
        file.name || null,
        `Replaced file on ${versionLabel} of "${item.name}"`
      ).catch(() => {});

      logger.info('[ReviewService] Version file replaced', { reviewId, itemId, versionId });
      return updatedVersion;
    } catch (error) {
      // 'Item not found' means the item was removed out from under the selection —
      // activeItemId can outlive its item after back-to-back removals. Telling the
      // user that is the difference between re-picking an item and retrying the
      // same dead id forever.
      if (error.message === 'Item not found') {
        logger.warn('[ReviewService] Replace target no longer exists', { reviewId, itemId, versionId });
        toastBridge.error('That item was removed. Select another item to replace its file.');
        throw error;
      }
      logger.error('[ReviewService] Failed to replace version file', { error: error.message, reviewId, itemId, versionId });
      toastBridge.error('Failed to replace file');
      throw error;
    }
  }

  /**
   * Self-heal a version's missing `duration` with a browser-resolved value.
   *
   * Some large progressive MP4s never resolve a usable `video.duration` on
   * certain browsers (intermittent, Windows-Chromium-leaning). The review
   * timeline's scrubber needs a duration, so when the player DOES resolve one
   * and the stored value was missing, persist it — then every later viewer,
   * including the flaky client, gets a working scrubber from the stored
   * fallback already in ReviewMediaViewer (emitDurationIfUsable / finishProbe).
   *
   * Idempotent + non-clobbering: only writes when the version still lacks a
   * usable duration, re-checked inside the transaction so a concurrent Bunny
   * transcode webhook write can't be overwritten (mirrors
   * onBunnyStreamReady.js:applyReviewVersionUpdate). Silent + auth-gated —
   * it's a background heal, and public share viewers must never write.
   */
  async updateVersionDuration(reviewId, itemId, versionId, duration) {
    if (!Number.isFinite(duration) || duration <= 0) return;
    if (!auth.currentUser) return; // public share viewers must not write
    try {
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(itemRef);
        if (!snap.exists()) return;
        const versions = [...(snap.data().versions || [])];
        const idx = versions.findIndex(v => v?.id === versionId);
        if (idx === -1) return;
        const existing = versions[idx].duration;
        if (Number.isFinite(existing) && existing > 0) return; // already filled — don't clobber
        versions[idx] = { ...versions[idx], duration: Math.round(duration * 1000) / 1000 };
        tx.update(itemRef, { versions });
      });
      logger.info('[ReviewService] Self-healed version duration', { reviewId, itemId, versionId, duration });
    } catch (error) {
      // Non-fatal: the in-memory fallback still drives the timeline this session.
      logger.warn('[ReviewService] Failed to self-heal version duration (non-fatal)', { error: error.message, reviewId, itemId, versionId });
    }
  }

  /**
   * Update item fields
   */
  async updateItem(reviewId, itemId, updates) {
    try {
      const user = auth.currentUser;
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);
      const loggedKeys = ['name', 'requiresFinalization', 'finalizationNote', 'internalStatus', 'clientStatus']
        .filter(k => k in updates);
      const before = loggedKeys.length ? (await getDoc(itemRef)).data() || {} : null;
      await updateDoc(itemRef, {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null
      });
      if (before) this._logItemUpdates(reviewId, before, updates, loggedKeys);
      logger.info('[ReviewService] Item updated', { reviewId, itemId });
    } catch (error) {
      logger.error('[ReviewService] Failed to update item', { error: error.message, reviewId, itemId });
      toastBridge.error('Failed to update item');
      throw error;
    }
  }

  /**
   * Delete an item, its Storage files, and associated comments
   * @param {string} [reason] - Optional context, e.g. 'client_post_download'
   *   to distinguish a client's post-download removal from a normal admin delete.
   */
  async deleteItem(reviewId, itemId, companyId, reason = null) {
    try {
      // Get item to find storage paths
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);
      const itemSnapshot = await getDoc(itemRef);

      let itemName = 'item';
      if (itemSnapshot.exists()) {
        const item = itemSnapshot.data();
        itemName = item.name || itemName;

        const effectiveCompanyId = companyId || await this._resolveCompanyIdForReview(reviewId);
        await Promise.all(
          (item.versions || []).map(v => this._deleteVersionArtifacts(v, effectiveCompanyId))
        );
      }

      // Delete associated comments
      const commentsRef = collection(db, 'reviews', reviewId, 'comments');
      const q = query(commentsRef, where('itemId', '==', itemId));
      const commentsSnapshot = await getDocs(q);
      const batch = writeBatch(db);

      commentsSnapshot.forEach((commentDoc) => {
        batch.delete(commentDoc.ref);
      });

      // Delete the item doc
      batch.delete(itemRef);
      await batch.commit();

      // If all items are removed, reset deliverable review state (fire-and-forget)
      const remainingItems = await getDocs(collection(db, 'reviews', reviewId, 'items'));
      if (remainingItems.empty) {
        this._resetDeliverableReviewState(reviewId, 'All review items removed').catch(() => {});
      }

      const isClientRemoval = reason === 'client_post_download';
      deliverableService.logActivity(
        reviewId,
        isClientRemoval ? 'review_client_removed' : 'review_item_deleted',
        null,
        itemName,
        null,
        isClientRemoval
          ? `Removed "${itemName}" from review after download`
          : `Deleted "${itemName}" from review`
      ).catch(() => {});

      logger.info('[ReviewService] Item deleted', { reviewId, itemId, reason });
    } catch (error) {
      logger.error('[ReviewService] Failed to delete item', { error: error.message, reviewId, itemId });
      toastBridge.error('Failed to delete item');
      throw error;
    }
  }

  /**
   * Clean up a review item after approval.
   * The approved file has already been copied to assets/, so all
   * review storage files, comments, and the item doc are deleted.
   */
  async cleanupApprovedItem(reviewId, itemId) {
    try {
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);
      const itemSnapshot = await getDoc(itemRef);

      if (itemSnapshot.exists()) {
        const item = itemSnapshot.data();
        const companyId = await this._resolveCompanyIdForReview(reviewId);
        await Promise.all(
          (item.versions || []).map(v => this._deleteVersionArtifacts(v, companyId))
        );
      }

      // Delete associated comments
      const commentsRef = collection(db, 'reviews', reviewId, 'comments');
      const q = query(commentsRef, where('itemId', '==', itemId));
      const commentsSnapshot = await getDocs(q);
      const batch = writeBatch(db);

      commentsSnapshot.forEach((commentDoc) => {
        batch.delete(commentDoc.ref);
      });

      // Delete the item doc
      batch.delete(itemRef);
      await batch.commit();

      // If all items are removed, reset deliverable review state
      const remainingItems = await getDocs(collection(db, 'reviews', reviewId, 'items'));
      if (remainingItems.empty) {
        this._resetDeliverableReviewState(reviewId, 'All review items removed').catch(() => {});
      }

      logger.info('[ReviewService] Approved item cleaned up', { reviewId, itemId });
    } catch (error) {
      logger.error('[ReviewService] Failed to clean up approved item', { error: error.message, reviewId, itemId });
      throw error;
    }
  }

  // ─── Full Review Deletion ─────────────────────────────────

  /**
   * Delete a review and all associated data: storage files, subcollections,
   * share links, and the review document itself.
   * Non-throwing — returns a result object so callers are never blocked.
   * @param {string} reviewId - The review (== deliverable) ID
   * @returns {Promise<{success: boolean, storageFilesDeleted: number}>}
   */
  async deleteReview(reviewId) {
    let storageFilesDeleted = 0;
    try {
      // Check if review doc exists — if not, nothing to clean up
      const reviewRef = doc(db, 'reviews', reviewId);
      const reviewSnapshot = await getDoc(reviewRef);
      if (!reviewSnapshot.exists()) {
        logger.info('[ReviewService] No review found for deletion, skipping', { reviewId });
        return { success: true, storageFilesDeleted: 0 };
      }

      // 1. Read all items + resolve the review's company for Bunny cleanup
      const review = reviewSnapshot.data();
      const companyId = review.companyId || null;
      const itemsRef = collection(db, 'reviews', reviewId, 'items');
      const itemsSnapshot = await getDocs(itemsRef);

      // 2. Delete all version artifacts (mixed legacy + Bunny) — best-effort
      const allVersions = itemsSnapshot.docs.flatMap(d => (d.data().versions || []));
      await Promise.all(allVersions.map(v => this._deleteVersionArtifacts(v, companyId)));
      for (const v of allVersions) {
        if (v.bunnyStream?.videoId || v.bunnyStorage?.path) storageFilesDeleted++;
      }

      // 3. Delete subcollections (items, comments, viewers)
      await deleteReviewSubcollections(reviewId);

      // 4. Delete reviewShareLinks where reviewId matches
      try {
        const shareLinksRef = collection(db, 'reviewShareLinks');
        const shareLinksQuery = query(shareLinksRef, where('reviewId', '==', reviewId));
        const shareLinksSnapshot = await getDocs(shareLinksQuery);

        if (!shareLinksSnapshot.empty) {
          const batch = writeBatch(db);
          shareLinksSnapshot.forEach((linkDoc) => {
            batch.delete(linkDoc.ref);
          });
          await batch.commit();
          logger.info('[ReviewService] Deleted review share links', {
            reviewId,
            count: shareLinksSnapshot.size
          });
        }
      } catch (error) {
        logger.warn('[ReviewService] Failed to delete share links for review', {
          reviewId,
          error: error.message
        });
      }

      // 5. Delete the review document itself
      await deleteDoc(reviewRef);

      logger.info('[ReviewService] Review fully deleted', { reviewId, storageFilesDeleted });
      return { success: true, storageFilesDeleted };
    } catch (error) {
      logger.error('[ReviewService] Failed to delete review', {
        error: error.message,
        reviewId,
        storageFilesDeleted
      });
      return { success: false, storageFilesDeleted };
    }
  }

  /**
   * Delete reviews for multiple deliverables (batch wrapper).
   * @param {string[]} deliverableIds - Array of deliverable IDs (reviewId === deliverableId)
   * @returns {Promise<{totalDeleted: number, errors: string[]}>}
   */
  async deleteReviewsForDeliverables(deliverableIds) {
    let totalDeleted = 0;
    const errors = [];

    for (const deliverableId of deliverableIds) {
      const result = await this.deleteReview(deliverableId);
      if (result.success) {
        totalDeleted++;
      } else {
        errors.push(deliverableId);
      }
    }

    logger.info('[ReviewService] Batch review deletion complete', {
      totalDeleted,
      errorCount: errors.length,
      total: deliverableIds.length
    });

    return { totalDeleted, errors };
  }

  /**
   * Subscribe to real-time item updates for a review
   */
  // `key` lets independent subscribers (review store vs. activity timeline) hold
  // their own listener for the same review — a shared key silently steals the
  // other one's snapshot stream and leaves it empty forever.
  subscribeToItems(reviewId, callback, { key = `items_${reviewId}`, onError } = {}) {
    this.unsubscribe(key);
    let retries = 0;

    const subscribe = () => {
      const itemsRef = collection(db, 'reviews', reviewId, 'items');
      const q = query(itemsRef, orderBy('sortOrder'));

      const unsub = onSnapshot(q, (snapshot) => {
        retries = 0;
        const items = {};
        snapshot.forEach((doc) => {
          items[doc.id] = { id: doc.id, ...doc.data() };
        });
        callback(items);
      }, (error) => {
        logger.firestoreListenerError('[ReviewService] Items subscription error', error, { reviewId });
        if (retries < 3) {
          retries++;
          const delay = 1000 * retries;
          logger.info('[ReviewService] Retrying items subscription', { reviewId, retry: retries });
          setTimeout(() => { if (this.listeners.has(key)) subscribe(); }, delay);
        } else if (onError) {
          onError(error);
        }
      });
      this.listeners.set(key, unsub);
      return unsub;
    };

    return subscribe();
  }

  /**
   * Promote the current version of an item to be client-facing.
   * Assigns the next clientVersionNumber. Idempotent — no-op if already promoted.
   * Returns the item data read during promotion (avoids redundant getDoc by callers).
   */
  async promoteVersionToClient(reviewId, itemId) {
    try {
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);

      // Read-modify-write the versions array in a transaction so a Bunny
      // transcode webhook landing between the read and the write can't be
      // clobbered by a stale whole-array write.
      let result = { promoted: false, itemData: null };
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(itemRef);
        if (!snap.exists()) { result = { promoted: false, itemData: null }; return; }

        const item = snap.data();
        const versions = item.versions || [];
        const currentVersion = versions.find(v => v.id === item.currentVersionId);
        if (!currentVersion) { result = { promoted: false, itemData: item }; return; }

        // Idempotent: already promoted
        if (currentVersion.clientVersionNumber != null) { result = { promoted: false, itemData: item }; return; }

        // Calculate next client version number
        const maxClientVersion = versions.reduce((max, v) => {
          return (v.clientVersionNumber != null && v.clientVersionNumber > max) ? v.clientVersionNumber : max;
        }, 0);
        const nextClientVersion = maxClientVersion + 1;

        // Update the version in the array
        const updatedVersions = versions.map(v => {
          if (v.id === item.currentVersionId) {
            return { ...v, clientVersionNumber: nextClientVersion };
          }
          return v;
        });

        // Reset client review status — the client hasn't reviewed this new version yet
        tx.update(itemRef, {
          versions: updatedVersions,
          clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          clientApprovedBy: null,
          clientApprovedAt: null,
          updatedAt: serverTimestamp()
        });

        result = {
          promoted: true,
          itemData: item,
          clientVersionNumber: nextClientVersion,
          versionLabel: currentVersion.label || `v${currentVersion.versionNumber}`
        };
      });

      if (!result.promoted) return result;

      deliverableService.logActivity(
        reviewId,
        'review_version_promoted',
        null,
        null,
        `C${result.clientVersionNumber}`,
        `${result.versionLabel} sent to client as C${result.clientVersionNumber} — ${result.itemData.name || 'item'}`
      ).catch(() => {});

      // Clear stale deliverable-level client review status (fire-and-forget)
      const deliverableRef = doc(db, 'deliverables', reviewId);
      getDoc(deliverableRef).then((snap) => {
        const oldStatus = snap.exists() ? snap.data().clientReviewStatus : null;
        return updateDoc(deliverableRef, {
          clientReviewStatus: null,
          clientReviewStatusAt: null,
          clientReviewStatusBy: null,
          clientReviewComplete: false,
          updatedAt: serverTimestamp()
        }).then(() => {
          this._logDeliverableFieldChange(reviewId, 'clientReviewStatus', oldStatus, null, 'Reset by new client version');
        });
      }).catch((err) => {
        logger.warn('[ReviewService] Failed to reset deliverable client review status on promote', {
          reviewId, error: err.message
        });
      });

      logger.info('[ReviewService] Version promoted to client', {
        reviewId, itemId, clientVersionNumber: result.clientVersionNumber
      });

      return result;
    } catch (error) {
      logger.error('[ReviewService] Failed to promote version', { error: error.message, reviewId, itemId });
      throw error;
    }
  }

  /**
   * Strip clientVersionNumber from the current version of an item.
   * Used when unapproving an internally approved version.
   */
  async demoteVersionFromClient(reviewId, itemId) {
    try {
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);

      // Read-modify-write the versions array in a transaction so a concurrent
      // Bunny transcode webhook write isn't clobbered by a stale whole-array write.
      let removedClientVersion = null;
      let demotedLabel = null;
      let demotedItemName = null;
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(itemRef);
        if (!snap.exists()) return;

        const item = snap.data();
        const versions = item.versions || [];
        const currentVersion = versions.find(v => v.id === item.currentVersionId);
        if (!currentVersion || currentVersion.clientVersionNumber == null) return;

        removedClientVersion = currentVersion.clientVersionNumber;
        demotedLabel = currentVersion.label || `v${currentVersion.versionNumber}`;
        demotedItemName = item.name || 'item';
        const updatedVersions = versions.map(v => {
          if (v.id === item.currentVersionId) {
            return { ...v, clientVersionNumber: null };
          }
          return v;
        });

        tx.update(itemRef, {
          versions: updatedVersions,
          clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          clientApprovedBy: null,
          clientApprovedAt: null,
          updatedAt: serverTimestamp()
        });
      });

      if (removedClientVersion == null) return;

      deliverableService.logActivity(
        reviewId,
        'review_version_demoted',
        null,
        `C${removedClientVersion}`,
        null,
        `${demotedLabel} (C${removedClientVersion}) withdrawn from client — ${demotedItemName}`
      ).catch(() => {});

      logger.info('[ReviewService] Version demoted from client', {
        reviewId, itemId, removedClientVersion
      });
    } catch (error) {
      logger.error('[ReviewService] Failed to demote version', { error: error.message, reviewId, itemId });
      throw error;
    }
  }

  /**
   * Update item approval status (internal or client)
   * @param {string} statusType - 'internal' or 'client'
   */
  async updateItemStatus(reviewId, itemId, statusType, status, userId, extraUpdates = {}) {
    try {
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);

      // Capture pre-update state for activity logging (non-blocking failure)
      let itemBefore = null;
      try {
        const beforeSnap = await getDoc(itemRef);
        if (beforeSnap.exists()) itemBefore = beforeSnap.data();
      } catch {
        // Logging context only — don't block the status update
      }

      const updates = {};

      if (statusType === 'internal') {
        updates.internalStatus = status;
        if (status === REVIEW_ITEM_STATUS.APPROVED) {
          updates.internalApprovedBy = userId;
          updates.internalApprovedAt = serverTimestamp();
        } else {
          updates.internalApprovedBy = null;
          updates.internalApprovedAt = null;
        }
      } else {
        updates.clientStatus = status;
        if (status === REVIEW_ITEM_STATUS.APPROVED) {
          updates.clientApprovedBy = userId;
          updates.clientApprovedAt = serverTimestamp();
        } else {
          updates.clientApprovedBy = null;
          updates.clientApprovedAt = null;
        }
        // Cascade: client changes_requested resets internal approval
        // so the item must pass internal review again before going back to client
        if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
          updates.internalStatus = REVIEW_ITEM_STATUS.IN_REVISION;
          updates.internalApprovedBy = null;
          updates.internalApprovedAt = null;
        }
      }

      // Record version number when changes are requested (for revision tracking)
      // Use caller-provided value to avoid an async getDoc that creates a race
      // window where onSnapshot can overwrite the optimistic update
      if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
        updates.changesRequestedAtVersion = extraUpdates.changesRequestedAtVersion || 1;
      }

      // NOTE: a raw whole-array `extraUpdates.versions` is intentionally NOT
      // accepted here — writing a caller-computed array clobbered concurrent
      // Bunny transcode webhook updates. The only version mutation through this
      // path (internal unapprove → demote current version off the client track)
      // is applied transactionally on fresh data below via demoteCurrentVersion.
      if (extraUpdates.clientStatus !== undefined && statusType === 'internal') {
        updates.clientStatus = extraUpdates.clientStatus;
        updates.clientApprovedBy = extraUpdates.clientApprovedBy ?? null;
        updates.clientApprovedAt = extraUpdates.clientApprovedAt ?? null;
      }

      // Approval asset metadata — saved on the item so the Cloud Function
      // can create the asset doc + copy files server-side
      if (extraUpdates.approvalAssetName !== undefined) {
        updates.approvalAssetName = extraUpdates.approvalAssetName;
      }
      if (extraUpdates.approvalAssetType !== undefined) {
        updates.approvalAssetType = extraUpdates.approvalAssetType;
      }
      if (extraUpdates.approvalTags !== undefined) {
        updates.approvalTags = extraUpdates.approvalTags;
      }

      updates.updatedAt = serverTimestamp();
      updates.updatedBy = userId;

      if (extraUpdates.demoteCurrentVersion) {
        // Strip clientVersionNumber from the current version on a FRESH read so
        // a concurrent Bunny webhook write to another version is preserved.
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(itemRef);
          if (!snap.exists()) throw new Error('Item not found');
          const data = snap.data();
          const versions = (data.versions || []).map(v =>
            v.id === data.currentVersionId ? { ...v, clientVersionNumber: null } : v
          );
          tx.update(itemRef, { ...updates, versions });
        });
      } else {
        await updateDoc(itemRef, updates);
      }
      logger.info('[ReviewService] Item status updated', { reviewId, itemId, statusType, status });

      this._logStatusChange(reviewId, itemBefore, statusType, status).catch(() => {});

      // Auto-promote version to client on internal approval
      if (statusType === 'internal' && status === REVIEW_ITEM_STATUS.APPROVED) {
        const { itemData } = await this.promoteVersionToClient(reviewId, itemId);

        // Complete finalization if item was pending finalization
        // Uses itemData from promoteVersionToClient to avoid a redundant getDoc
        if (itemData?.finalizationStatus === FINALIZATION_STATUS.PENDING) {
          await updateDoc(itemRef, {
            finalizationStatus: FINALIZATION_STATUS.COMPLETE,
            clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
            clientApprovedBy: null,
            clientApprovedAt: null,
            updatedAt: serverTimestamp(),
            updatedBy: userId
          });
          logger.info('[ReviewService] Finalization completed on internal approval', { reviewId, itemId });
          deliverableService.logActivity(
            reviewId,
            'review_finalization_completed',
            null,
            null,
            `v${itemData.currentVersionNumber || 1}`,
            `Finalization completed for "${itemData.name || 'item'}" with v${itemData.currentVersionNumber || 1}`
          ).catch(() => {});

          // Sync finalization state to deliverable
          this._syncFinalizationToDeliverable(reviewId, userId).catch((err) => {
            logger.warn('[ReviewService] Finalization sync failed', { reviewId, error: err.message });
          });
        }
      }

      // Sync review actions to deliverable for automation. PENDING_REVIEW is
      // included so that reverting an approval clears a completed aggregate —
      // see the hasPending branch in syncReviewStateToDeliverable.
      if (
        status === REVIEW_ITEM_STATUS.APPROVED ||
        status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED ||
        status === REVIEW_ITEM_STATUS.PENDING_REVIEW
      ) {
        this.syncReviewStateToDeliverable(reviewId, statusType, status, userId, itemId)
          .then((result) => {
            // After changes_requested is written, cascade to in_revision
            // so automation rules can trigger on "changed_from in_revision"
            if (result?.synced && result.aggregateStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
              return this._syncInternalRevisionToDeliverable(reviewId, userId);
            }
          })
          .catch((err) => {
            logger.warn('[ReviewService] Review-to-deliverable sync failed', {
              reviewId, itemId, error: err.message
            });
          });
      }
    } catch (error) {
      logger.error('[ReviewService] Failed to update item status', { error: error.message, reviewId, itemId });
      toastBridge.error('Failed to update item status');
      throw error;
    }
  }

  /**
   * Approve item for finalization (first client approval when requiresFinalization is true).
   * Sets clientStatus to 'pending_finalization', finalizationStatus to 'pending',
   * records approval metadata, and cascades internalStatus to 'in_revision'.
   */
  async approveForFinalization(reviewId, itemId, userId) {
    try {
      const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);

      let itemName = 'item';
      let versionLabel = 'v1';
      try {
        const beforeSnap = await getDoc(itemRef);
        if (beforeSnap.exists()) {
          const d = beforeSnap.data();
          itemName = d.name || itemName;
          versionLabel = `v${d.currentVersionNumber || 1}`;
        }
      } catch {
        // Logging context only
      }

      const updates = {
        clientStatus: REVIEW_ITEM_STATUS.PENDING_FINALIZATION,
        finalizationStatus: FINALIZATION_STATUS.PENDING,
        clientApprovedBy: userId,
        clientApprovedAt: serverTimestamp(),
        // Cascade: reset internal status so admin must re-review with finalized version
        internalStatus: REVIEW_ITEM_STATUS.IN_REVISION,
        internalApprovedBy: null,
        internalApprovedAt: null,
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };

      await updateDoc(itemRef, updates);
      logger.info('[ReviewService] Item approved for finalization', { reviewId, itemId });

      deliverableService.logActivity(
        reviewId,
        'review_client_approved_finalization',
        'clientStatus',
        null,
        REVIEW_ITEM_STATUS.PENDING_FINALIZATION,
        `Client approved "${itemName}" ${versionLabel} for finalization`
      ).catch(() => {});

      // Sync finalization state to deliverable
      this._syncFinalizationToDeliverable(reviewId, userId).catch((err) => {
        logger.warn('[ReviewService] Finalization sync failed', { reviewId, error: err.message });
      });

      // Sync review state (pending_finalization counts as not fully approved)
      this.syncReviewStateToDeliverable(reviewId, 'client', REVIEW_ITEM_STATUS.PENDING_FINALIZATION, userId, itemId)
        .catch((err) => {
          logger.warn('[ReviewService] Review-to-deliverable sync failed after finalization approval', {
            reviewId, itemId, error: err.message
          });
        });
    } catch (error) {
      logger.error('[ReviewService] Failed to approve for finalization', { error: error.message, reviewId, itemId });
      toastBridge.error('Failed to approve for finalization');
      throw error;
    }
  }

  /**
   * Reorder items via batch update
   */
  async reorderItems(reviewId, itemUpdates) {
    try {
      const batch = writeBatch(db);

      for (const { itemId, sortOrder } of itemUpdates) {
        const itemRef = doc(db, 'reviews', reviewId, 'items', itemId);
        batch.update(itemRef, { sortOrder });
      }

      await batch.commit();
      deliverableService.logActivity(
        reviewId, 'review_items_reordered', null, null, null, `Reordered ${itemUpdates.length} items`
      ).catch(() => {});
      logger.info('[ReviewService] Items reordered', { reviewId, count: itemUpdates.length });
    } catch (error) {
      logger.error('[ReviewService] Failed to reorder items', { error: error.message, reviewId });
      toastBridge.error('Failed to reorder items');
      throw error;
    }
  }

  // ─── Comments ──────────────────────────────────────────────

  /**
   * Add a comment to a review
   */
  async addComment(reviewId, commentData) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const userName = await this._getUserName(user);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};

      const isReply = !!commentData.parentId;

      const comment = {
        reviewId,
        itemId: commentData.itemId,
        versionId: commentData.versionId,
        parentId: commentData.parentId || null,
        text: commentData.text.trim(),
        timestamp: isReply ? null : (commentData.timestamp ?? null),
        timestampEnd: isReply ? null : (commentData.timestampEnd ?? null),
        resolved: isReply ? null : false,
        resolvedBy: null,
        resolvedAt: null,
        userId: user.uid,
        userName,
        userEmail: user.email,
        userType: userData.systemRole || userData.userType || 'user',
        userAvatar: userData.profile?.avatar || userData.avatar || null,
        edited: false,
        editedAt: null,
        createdAt: serverTimestamp()
      };

      const commentsRef = collection(db, 'reviews', reviewId, 'comments');
      const docRef = await addDoc(commentsRef, comment);

      this._getItemName(reviewId, commentData.itemId).then((itemName) => {
        return deliverableService.logActivity(
          reviewId,
          isReply ? 'review_reply_added' : 'review_comment_added',
          null,
          null,
          null,
          isReply
            ? `Replied to a comment on "${itemName}"`
            : `Added a comment on "${itemName}"`
        );
      }).catch(() => {});

      logger.info('[ReviewService] Comment added', { reviewId, commentId: docRef.id });
      return { id: docRef.id, ...comment };
    } catch (error) {
      logger.error('[ReviewService] Failed to add comment', { error: error.message, reviewId });
      toastBridge.error('Failed to add comment');
      throw error;
    }
  }

  /**
   * Update a comment's text
   */
  async updateComment(reviewId, commentId, updates) {
    try {
      const commentRef = doc(db, 'reviews', reviewId, 'comments', commentId);
      await updateDoc(commentRef, {
        text: updates.text.trim(),
        edited: true,
        editedAt: serverTimestamp()
      });
      const text = updates.text.trim();
      const snippet = text.length > 60 ? `${text.slice(0, 60)}…` : text;
      deliverableService.logActivity(
        reviewId, 'review_comment_edited', null, null, null, `Edited a comment: "${snippet}"`
      ).catch(() => {});
      logger.info('[ReviewService] Comment updated', { reviewId, commentId });
    } catch (error) {
      logger.error('[ReviewService] Failed to update comment', { error: error.message, reviewId, commentId });
      toastBridge.error('Failed to update comment');
      throw error;
    }
  }

  /**
   * Hard delete a comment. If top-level, cascade-deletes all replies.
   */
  async deleteComment(reviewId, commentId) {
    try {
      const commentRef = doc(db, 'reviews', reviewId, 'comments', commentId);
      const commentSnapshot = await getDoc(commentRef);

      // Already gone (double-click on delete). Deleting a missing doc makes the
      // rules read `resource.data.userId` off a null resource, which errors out
      // as "Missing or insufficient permissions" — a false failure toast for a
      // delete that already succeeded.
      if (!commentSnapshot.exists()) {
        logger.info('[ReviewService] Comment already deleted', { reviewId, commentId });
        return;
      }

      const cd = commentSnapshot.data();
      const logItemId = cd.itemId || null;
      const isTopLevel = !cd.parentId;
      let cascadeReplyCount = 0;

      if (isTopLevel) {
        // Top-level comment — batch-delete it and all its replies
        const commentsRef = collection(db, 'reviews', reviewId, 'comments');
        const repliesQuery = query(commentsRef, where('parentId', '==', commentId));
        const repliesSnapshot = await getDocs(repliesQuery);
        cascadeReplyCount = repliesSnapshot.size;

        const batch = writeBatch(db);
        repliesSnapshot.forEach((replyDoc) => {
          batch.delete(replyDoc.ref);
        });
        batch.delete(commentRef);
        await batch.commit();
      } else {
        // Reply — just delete the single doc
        await deleteDoc(commentRef);
      }

      (logItemId ? this._getItemName(reviewId, logItemId) : Promise.resolve(null)).then((itemName) => {
        const onItem = itemName ? ` on "${itemName}"` : '';
        const cascadeNote = cascadeReplyCount > 0
          ? ` (${cascadeReplyCount} repl${cascadeReplyCount === 1 ? 'y' : 'ies'} also removed)`
          : '';
        return deliverableService.logActivity(
          reviewId,
          'review_comment_deleted',
          null,
          null,
          null,
          `Deleted ${isTopLevel ? 'a comment' : 'a reply'}${onItem}${cascadeNote}`
        );
      }).catch(() => {});

      logger.info('[ReviewService] Comment deleted', { reviewId, commentId });
    } catch (error) {
      logger.error('[ReviewService] Failed to delete comment', { error: error.message, reviewId, commentId });
      toastBridge.error('Failed to delete comment');
      throw error;
    }
  }

  /**
   * Toggle resolved state on a comment
   */
  async toggleCommentResolved(reviewId, commentId, resolved, userId) {
    try {
      const commentRef = doc(db, 'reviews', reviewId, 'comments', commentId);

      let logItemId = null;
      try {
        const beforeSnap = await getDoc(commentRef);
        if (beforeSnap.exists()) logItemId = beforeSnap.data().itemId || null;
      } catch {
        // Logging context only
      }

      const updates = {
        resolved,
        resolvedBy: resolved ? userId : null,
        resolvedAt: resolved ? serverTimestamp() : null
      };
      await updateDoc(commentRef, updates);
      logger.info('[ReviewService] Comment resolved toggled', { reviewId, commentId, resolved });

      (logItemId ? this._getItemName(reviewId, logItemId) : Promise.resolve(null)).then((itemName) => {
        const onItem = itemName ? ` on "${itemName}"` : '';
        return deliverableService.logActivity(
          reviewId,
          resolved ? 'review_comment_resolved' : 'review_comment_unresolved',
          'resolved',
          !resolved,
          resolved,
          resolved
            ? `Marked a comment resolved${onItem}`
            : `Marked a comment unresolved${onItem}`
        );
      }).catch(() => {});
    } catch (error) {
      logger.error('[ReviewService] Failed to toggle comment resolved', { error: error.message, reviewId, commentId });
      toastBridge.error('Failed to update comment');
      throw error;
    }
  }

  /**
   * Bulk-resolve every comment under a review (top-level threads AND replies).
   * Used by the "Skip Internal Review" admin action when there are unresolved
   * internal comments that need to be marked resolved before promoting to client.
   */
  async bulkResolveAllComments(reviewId, userId) {
    try {
      const commentsRef = collection(db, 'reviews', reviewId, 'comments');
      const snapshot = await getDocs(commentsRef);
      const batch = writeBatch(db);
      let updatedCount = 0;

      snapshot.forEach((commentDoc) => {
        if (commentDoc.data().resolved === true) return;
        batch.update(commentDoc.ref, {
          resolved: true,
          resolvedBy: userId,
          resolvedAt: serverTimestamp()
        });
        updatedCount++;
      });

      if (updatedCount === 0) {
        return { resolved: 0 };
      }

      await batch.commit();
      deliverableService.logActivity(
        reviewId, 'review_comments_bulk_resolved', null, null, updatedCount, `${updatedCount} comments`
      ).catch(() => {});
      logger.info('[ReviewService] Bulk resolved comments', { reviewId, count: updatedCount });
      return { resolved: updatedCount };
    } catch (error) {
      logger.error('[ReviewService] Failed to bulk resolve comments', { error: error.message, reviewId });
      toastBridge.error('Failed to resolve comments');
      throw error;
    }
  }

  /**
   * Subscribe to real-time comment updates
   */
  subscribeToComments(reviewId, callback, { key = `comments_${reviewId}`, onError } = {}) {
    this.unsubscribe(key);
    let retries = 0;

    const subscribe = () => {
      const commentsRef = collection(db, 'reviews', reviewId, 'comments');
      const q = query(commentsRef, orderBy('createdAt'));

      const unsub = onSnapshot(q, (snapshot) => {
        retries = 0;
        const comments = [];
        snapshot.forEach((doc) => {
          comments.push({ id: doc.id, ...doc.data() });
        });
        callback(comments);
      }, (error) => {
        logger.firestoreListenerError('[ReviewService] Comments subscription error', error, { reviewId });
        if (retries < 3) {
          retries++;
          const delay = 1000 * retries;
          logger.info('[ReviewService] Retrying comments subscription', { reviewId, retry: retries });
          setTimeout(() => { if (this.listeners.has(key)) subscribe(); }, delay);
        } else if (onError) {
          onError(error);
        }
      });
      this.listeners.set(key, unsub);
      return unsub;
    };

    return subscribe();
  }

  // ─── File Upload ───────────────────────────────────────────

  /**
   * Upload a review version file. Uses Bunny (Stream for videos, Storage for
   * everything else) via bunnyUploadService, returning the Bunny-specific
   * pointer fields the caller will embed on the version object.
   *
   * `onTask` receives a { pause, resume } handle once the resumable (TUS video)
   * upload starts, so the upload queue can wire manual pause/resume and the
   * stall-recovery watchdog. Non-video uploads are small XHR POSTs with no
   * resumable handle, so onTask simply never fires for them.
   *
   * @returns {Promise<{ bunnyStream?: object, bunnyStorage?: object }>}
   */
  async uploadReviewFile(companyId, reviewId, itemId, versionId, file, onProgress, onTask) {
    try {
      const result = await uploadToBunny({
        file,
        companyId,
        context: { type: BUNNY_UPLOAD_CONTEXT.REVIEW_VERSION, reviewId, itemId, versionId },
        onProgress,
        onTask,
      });
      logger.info('[ReviewService] File uploaded to Bunny', {
        reviewId, itemId, versionId, kind: result.kind,
      });
      const out = {};
      if (result.bunnyStream) out.bunnyStream = result.bunnyStream;
      if (result.bunnyStorage) out.bunnyStorage = result.bunnyStorage;
      return out;
    } catch (err) {
      logger.error('[ReviewService] Bunny upload failed', {
        error: err.message, reviewId, itemId, versionId,
      });
      throw err;
    }
  }

  // NOTE: A client-side copyFileToAssets() (getBlob + uploadBytes) was removed here.
  // It loaded entire approved videos into browser memory and could OOM-crash the tab.
  // Promote-on-approval is handled entirely server-side by the onReviewItemWrite
  // Firestore trigger (functions/reviews/index.js → copyApprovedItemToAssets), which
  // uses bucket.file().copy() with no in-memory download.

  /**
   * Resolve a review's companyId from Firestore — used by delete helpers
   * that take a reviewId but no companyId argument. Returns null if the
   * review doc is missing.
   */
  async _resolveCompanyIdForReview(reviewId) {
    try {
      const snap = await getDoc(doc(db, 'reviews', reviewId));
      return snap.exists() ? (snap.data().companyId || null) : null;
    } catch (err) {
      logger.warn('[ReviewService] Failed to resolve review companyId', { reviewId, error: err.message });
      return null;
    }
  }

  /**
   * Delete a version's backing storage artifacts. Handles both Bunny-era
   * versions (bunnyStream.videoId / bunnyStorage.path) and legacy versions
   * (storagePath / thumbnailStoragePath). Best-effort — never throws so a
   * single failure doesn't block the surrounding multi-version cleanup.
   */
  async _deleteVersionArtifacts(version, companyId) {
    if (!version) return;
    const tasks = [];

    const hasBunny = !!(version.bunnyStream?.videoId || version.bunnyStorage?.path);
    if (hasBunny) {
      if (!companyId) {
        logger.warn('[ReviewService] companyId missing — cannot clean up Bunny artifacts', {
          versionId: version.id,
        });
      } else {
        const callable = httpsCallable(functions, 'deleteBunnyArtifacts');
        tasks.push(
          callable({
            companyId,
            videoId: version.bunnyStream?.videoId,
            storagePath: version.bunnyStorage?.path,
          }).catch((err) => logger.warn('[ReviewService] deleteBunnyArtifacts failed (non-fatal)', {
            error: err.message, versionId: version.id,
          }))
        );
      }
    }

    await Promise.all(tasks);
  }

  // ─── Aggregates (client-side fallback) ─────────────────────

  /**
   * Recalculate aggregate counters on review doc
   * Used as fallback if Cloud Function hasn't run yet
   */
  async recalculateAggregates(reviewId) {
    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      const reviewSnapshot = await getDoc(reviewRef);
      if (!reviewSnapshot.exists()) return;

      // Count items (using clientStatus as the client-facing status)
      const itemsRef = collection(db, 'reviews', reviewId, 'items');
      const itemsSnapshot = await getDocs(itemsRef);

      let itemCount = 0;
      let approvedCount = 0;
      let changesRequestedCount = 0;
      let pendingCount = 0;

      itemsSnapshot.forEach((itemDoc) => {
        itemCount++;
        const item = itemDoc.data();
        const status = item.clientStatus;
        if (status === REVIEW_ITEM_STATUS.APPROVED) approvedCount++;
        else if (status === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) changesRequestedCount++;
        else pendingCount++;
      });

      // Count comments
      const commentsRef = collection(db, 'reviews', reviewId, 'comments');
      const commentsSnapshot = await getDocs(commentsRef);
      const commentCount = commentsSnapshot.size;

      await updateDoc(reviewRef, {
        itemCount,
        approvedCount,
        changesRequestedCount,
        pendingCount,
        commentCount
      });

      logger.info('[ReviewService] Aggregates recalculated', { reviewId, itemCount, commentCount });
    } catch (error) {
      logger.error('[ReviewService] Failed to recalculate aggregates', { error: error.message, reviewId });
    }
  }

  // ─── Review → Deliverable Sync ──────────────────────────

  /**
   * Sync review state to the deliverable document and trigger automation rules.
   * Only fires once ALL items have been reviewed (no pending_review left).
   * Status is the aggregate outcome: 'changes_requested' if any item needs
   * changes, 'approved' only if every item is approved.
   * @param {string} statusType - 'internal' or 'client'
   */
  /**
   * Walk a completed review aggregate back to pending_review when an item re-opens.
   * No-ops unless the deliverable currently claims a terminal verdict, so a normal
   * first pass (aggregate still null) writes nothing and automation isn't thrashed.
   * ponytail: deliberately does NOT evaluate rules — nothing should fire on "went
   * back to pending"; the point is only to restore the transition the next
   * completing approval needs.
   */
  async _clearStaleReviewAggregate(deliverableId, prefix, userId) {
    try {
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const snap = await getDoc(deliverableRef);
      const current = snap.exists() ? snap.data()[`${prefix}Status`] : null;
      if (
        current !== REVIEW_ITEM_STATUS.APPROVED &&
        current !== REVIEW_ITEM_STATUS.CHANGES_REQUESTED
      ) {
        return;
      }
      await updateDoc(deliverableRef, {
        [`${prefix}Status`]: REVIEW_ITEM_STATUS.PENDING_REVIEW,
        [`${prefix}StatusAt`]: serverTimestamp(),
        [`${prefix}StatusBy`]: userId,
        [`${prefix}Complete`]: false,
        updatedAt: serverTimestamp()
      });
      this._logDeliverableFieldChange(deliverableId, `${prefix}Status`, current, REVIEW_ITEM_STATUS.PENDING_REVIEW, 'Review reopened');
      logger.info('[ReviewService] Cleared stale review aggregate', {
        deliverableId, prefix, from: current
      });
    } catch (error) {
      logger.warn('[ReviewService] Failed to clear stale review aggregate', {
        deliverableId, prefix, error: error.message
      });
    }
  }

  async syncReviewStateToDeliverable(reviewId, statusType, action, userId, updatedItemId) {
    try {
      const deliverableId = reviewId; // reviewId === deliverableId (1:1 mapping)
      const prefix = statusType === 'internal' ? 'internalReview' : 'clientReview';
      const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';

      // Query all review items to compute aggregate status
      const itemsRef = collection(db, 'reviews', reviewId, 'items');
      const itemsSnapshot = await getDocs(itemsRef);

      // Terminal client decisions. Allowlist (not denylist) so that null/undefined/
      // pending_review/in_revision all count as NOT decided by construction — a null
      // clientStatus is a real mid-review state and must never read as "decided".
      // changes_requested IS decided: the client acted, and revision_queue (where the rule
      // sends it) is the right destination; the rule's stage/finalization conditions guard it.
      const CLIENT_DECIDED_STATUSES = [
        REVIEW_ITEM_STATUS.APPROVED,
        REVIEW_ITEM_STATUS.CHANGES_REQUESTED,
        REVIEW_ITEM_STATUS.PENDING_FINALIZATION
      ];

      let hasPending = false;
      let hasChangesRequested = false;
      let allClientDecided = true; // every item has a terminal client decision (allowlist)
      let itemCount = 0;
      itemsSnapshot.forEach((itemDoc) => {
        itemCount++;
        // Use known-correct status for the item we just updated to avoid stale reads
        const itemStatus = itemDoc.id === updatedItemId
          ? action
          : itemDoc.data()[statusField];
        if (itemStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW || itemStatus === REVIEW_ITEM_STATUS.IN_REVISION || itemStatus === REVIEW_ITEM_STATUS.PENDING_FINALIZATION) {
          hasPending = true;
        } else if (itemStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
          hasChangesRequested = true;
        }
        if (!CLIENT_DECIDED_STATUSES.includes(itemStatus)) {
          allClientDecided = false;
        }
      });

      // clientReviewDecided: the client has reached a terminal decision on EVERY item. This
      // lets finalization automation move the deliverable only after the FULL client pass —
      // instead of yanking it out of client_review on the first approved-for-finalization
      // item. Synced on both the early-return (finalization-pending) and full-sync paths,
      // so it must run here, before the hasPending early return.
      // Per-slot revision flags. Runs BEFORE _syncClientReviewDecided and before the
      // hasPending early return, and the order is load-bearing: rules that trigger on
      // clientReviewDecided read these flags as CONDITIONS, so the flags must already be
      // durable when that flag flips — otherwise the condition reads a stale false and
      // the rule silently never fires.
      await this._syncSlotChangeFlags(deliverableId, statusType, userId, updatedItemId, action);

      if (statusType === 'client') {
        await this._syncClientReviewDecided(deliverableId, itemCount > 0 && allClientDecided, userId);
      }

      // Only sync when all items have been reviewed — with one exception: if the
      // aggregate still claims a terminal verdict, an item just re-opened and that
      // verdict is now a lie. Leaving it stale also breaks re-triggering, because
      // the next completing approval writes "approved" over "approved" and rules
      // with a `changed_to` trigger require oldValue !== newValue (ruleEngine.js).
      if (hasPending) {
        await this._clearStaleReviewAggregate(deliverableId, prefix, userId);
        return { synced: false };
      }

      const aggregateStatus = hasChangesRequested ? REVIEW_ITEM_STATUS.CHANGES_REQUESTED : REVIEW_ITEM_STATUS.APPROVED;

      // Read current deliverable (needed as oldData for automation diff)
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      if (!deliverableSnap.exists()) {
        logger.warn('[ReviewService] Deliverable not found for review sync', { reviewId });
        return;
      }
      const oldData = deliverableSnap.data();

      // Use plain Date for httpsCallable (serverTimestamp() sentinel can't serialize)
      const automationNewData = {
        [`${prefix}Status`]: aggregateStatus,
        [`${prefix}StatusAt`]: new Date().toISOString(),
        [`${prefix}StatusBy`]: userId,
        [`${prefix}Complete`]: aggregateStatus === REVIEW_ITEM_STATUS.APPROVED
      };

      // Write review state to deliverable
      const reviewFields = {
        [`${prefix}Status`]: aggregateStatus,
        [`${prefix}StatusAt`]: serverTimestamp(),
        [`${prefix}StatusBy`]: userId,
        [`${prefix}Complete`]: aggregateStatus === REVIEW_ITEM_STATUS.APPROVED
      };
      await updateDoc(deliverableRef, {
        ...reviewFields,
        automationPending: this._pendingAutomationMarker(oldData, automationNewData),
        updatedAt: serverTimestamp()
      });

      this._logDeliverableFieldChange(deliverableId, `${prefix}Status`, oldData[`${prefix}Status`], aggregateStatus, 'Synced from review');
      logger.info('[ReviewService] Synced review state to deliverable', {
        reviewId, deliverableId, statusType, aggregateStatus
      });

      // Trigger automation rules (fire-and-forget)
      // Strip Firestore Timestamps from oldData so httpsCallable serializes cleanly
      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const evaluateRules = httpsCallable(functions, 'evaluateDeliverableRules');
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: automationNewData,
        triggerType: 'field_change'
      }).then((result) => {
        this._clearPendingAutomation(deliverableRef);
        if (result.data?.rulesExecuted > 0) {
          logger.info('[ReviewService] Automation rules executed after review sync', {
            deliverableId, rulesExecuted: result.data.rulesExecuted
          });
        }
      }).catch((err) => {
        // Marker deliberately left in place — retryPendingAutomation replays it.
        logger.warn('[ReviewService] Automation evaluation failed', { deliverableId, error: err.message });
      });

      return { synced: true, aggregateStatus };

    } catch (error) {
      // Non-blocking: the review item status was already updated successfully
      logger.error('[ReviewService] Failed to sync review state to deliverable', {
        error: error.message, reviewId, statusType
      });
      return { synced: false };
    }
  }

  /**
   * Build the durable retry marker for an automation call.
   *
   * The evaluateDeliverableRules call below every sync is fire-and-forget from the
   * browser: the Firestore write is durable, the callable is not. A tab closed in
   * that gap leaves the deliverable in its new state with no rule ever evaluated —
   * which is how a client-approved deliverable ends up stranded in client_review.
   * Stamping this in the SAME write as the data change means the intent survives
   * whatever happens to the page; the `retryPendingAutomation` sweep replays any
   * marker still present after two minutes.
   */
  _pendingAutomationMarker(oldData, newValues) {
    const oldValues = {};
    for (const key of Object.keys(newValues)) {
      oldValues[key] = oldData[key] ?? null;
    }
    return { oldValues, newValues, at: serverTimestamp() };
  }

  /**
   * Clear the retry marker once the automation call has actually landed.
   * Best-effort: a failure here just means the sweep replays a call that already
   * ran, and rule conditions are re-checked against live state, so that's a no-op.
   */
  _clearPendingAutomation(deliverableRef) {
    updateDoc(deliverableRef, { automationPending: deleteField() }).catch(() => {});
  }

  /**
   * Write the `clientReviewDecided` aggregate flag to the deliverable and fire automation
   * if (and only if) it changed. True once the client has acted on every item — including
   * items approved-for-finalization (pending_finalization), which the main client-review
   * aggregate treats as "still pending" and therefore never surfaces.
   *
   * This is the signal the "Finalization Needed" automation rule triggers on, so that a
   * multi-item deliverable moves to revision_queue ONCE (after the full client pass) rather
   * than thrashing out of client_review on each per-item finalization request.
   */
  async _syncClientReviewDecided(deliverableId, decided, userId) {
    try {
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      if (!deliverableSnap.exists()) return;

      const oldData = deliverableSnap.data();
      const current = oldData.clientReviewDecided ?? false;
      if (current === decided) return; // no change — skip write + automation

      // Race guard: this runs per item, fire-and-forget. When several items are decided
      // in quick succession the calls complete out of order, and a stale one (that read
      // the item set before its siblings committed) can try to downgrade a correct
      // `true` back to `false` — so the flag never lands on true and the "Finalization
      // Needed" rule (trigger: changed_to true) never fires. Before any true→false
      // downgrade, re-read the items fresh; if they are in fact ALL decided, a fresher
      // sibling already set true correctly, so skip the clobber. (A genuine reset to
      // false still writes — those items really aren't all decided.)
      if (decided === false && current === true) {
        const itemsSnapshot = await getDocs(collection(db, 'reviews', deliverableId, 'items'));
        let liveCount = 0;
        let liveAllDecided = true;
        itemsSnapshot.forEach((itemDoc) => {
          liveCount++;
          if (isActionableStatus(itemDoc.data().clientStatus)) liveAllDecided = false;
        });
        if (liveCount > 0 && liveAllDecided) {
          logger.info('[ReviewService] Skipped stale clientReviewDecided true→false clobber', { deliverableId });
          return;
        }
      }

      await updateDoc(deliverableRef, {
        clientReviewDecided: decided,
        clientReviewDecidedAt: decided ? serverTimestamp() : null,
        clientReviewDecidedBy: decided ? userId : null,
        automationPending: this._pendingAutomationMarker(oldData, { clientReviewDecided: decided }),
        updatedAt: serverTimestamp()
      });
      this._logDeliverableFieldChange(deliverableId, 'clientReviewDecided', current, decided, 'Synced from review');
      logger.info('[ReviewService] Synced clientReviewDecided to deliverable', { deliverableId, decided });

      // Trigger automation rules (fire-and-forget). The generic rule engine diffs newData
      // against oldData, so the new field needs no Cloud Function change.
      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const evaluateRules = httpsCallable(functions, 'evaluateDeliverableRules');
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: { clientReviewDecided: decided },
        triggerType: 'field_change'
      }).then(() => {
        this._clearPendingAutomation(deliverableRef);
      }).catch((err) => {
        logger.warn('[ReviewService] Automation evaluation failed after clientReviewDecided sync', {
          deliverableId, error: err.message
        });
      });
    } catch (error) {
      // Non-blocking: the review item status was already updated successfully
      logger.warn('[ReviewService] Failed to sync clientReviewDecided', {
        error: error.message, deliverableId
      });
    }
  }

  /**
   * Mirror each podcast slot's "changes requested" state onto the deliverable as flat
   * booleans (clientReviewSlotChanges_thumbnail, …) so automation rules can trigger on
   * "the thumbnail has revisions" without knowing anything about review subcollections.
   *
   * Re-reads items rather than taking a snapshot argument: the resubmit/reset callers
   * only have a pre-batch snapshot, and the extra read is ≤7 docs.
   */
  async _syncSlotChangeFlags(deliverableId, statusType, userId, updatedItemId, action) {
    try {
      const prefix = statusType === 'internal' ? 'internalReview' : 'clientReview';
      const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';

      const itemsSnapshot = await getDocs(collection(db, 'reviews', deliverableId, 'items'));
      // Same stale-read guard the caller uses: trust the known status of the item we just wrote.
      const items = itemsSnapshot.docs.map((d) => (
        d.id === updatedItemId && action ? { ...d.data(), [statusField]: action } : d.data()
      ));
      const flags = computeSlotChangeFlags(items, statusField, prefix);

      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      if (!deliverableSnap.exists()) return;
      const oldData = deliverableSnap.data();

      // Only the flags that actually moved. A free-form (non-slotted) review produces all
      // false against all-undefined, which diffs to nothing — so non-podcast deliverables
      // never get these fields written at all.
      const changed = {};
      for (const [key, value] of Object.entries(flags)) {
        if ((oldData[key] ?? false) !== value) changed[key] = value;
      }
      if (Object.keys(changed).length === 0) return;

      await updateDoc(deliverableRef, {
        ...changed,
        automationPending: this._pendingAutomationMarker(oldData, changed),
        updatedAt: serverTimestamp()
      });
      for (const [key, value] of Object.entries(changed)) {
        this._logDeliverableFieldChange(deliverableId, key, oldData[key] ?? false, value, 'Synced from review');
      }
      logger.info('[ReviewService] Synced slot change flags', { deliverableId, changed });

      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const evaluateRules = httpsCallable(functions, 'evaluateDeliverableRules');
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: changed,
        triggerType: 'field_change'
      }).then(() => {
        this._clearPendingAutomation(deliverableRef);
      }).catch((err) => {
        logger.warn('[ReviewService] Automation evaluation failed after slot flag sync', {
          deliverableId, error: err.message
        });
      });
    } catch (error) {
      // Non-blocking: the review item status was already updated successfully
      logger.warn('[ReviewService] Failed to sync slot change flags', {
        error: error.message, deliverableId
      });
    }
  }

  // ─── Resubmit for Review ─────────────────────────────────

  /**
   * Resubmit items for internal review after revisions are complete.
   * Resets all changes_requested items to pending_review, clears version tracking,
   * updates the deliverable, and triggers automation rules.
   */
  async resubmitForInternalReview(reviewId, statusType, userId) {
    try {
      const deliverableId = reviewId;
      const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';
      const prefix = statusType === 'internal' ? 'internalReview' : 'clientReview';

      // Query all items and batch-reset changes_requested → pending_review
      const itemsRef = collection(db, 'reviews', reviewId, 'items');
      const itemsSnapshot = await getDocs(itemsRef);
      const batch = writeBatch(db);
      let resetCount = 0;

      itemsSnapshot.forEach((itemDoc) => {
        const itemStatus = itemDoc.data()[statusField];
        if (itemStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED || itemStatus === REVIEW_ITEM_STATUS.IN_REVISION) {
          batch.update(itemDoc.ref, {
            [statusField]: REVIEW_ITEM_STATUS.PENDING_REVIEW,
            changesRequestedAtVersion: null,
            updatedAt: serverTimestamp(),
            updatedBy: userId
          });
          resetCount++;
        }
      });

      await batch.commit();

      if (resetCount > 0) {
        const phaseLabel = statusType === 'internal' ? 'internal review' : 'client review';
        deliverableService.logActivity(
          reviewId,
          'review_resubmitted',
          null,
          null,
          resetCount,
          `Resubmitted ${resetCount} item${resetCount === 1 ? '' : 's'} for ${phaseLabel}`
        ).catch(() => {});
      }

      // Read current deliverable as oldData for automation diff
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      if (!deliverableSnap.exists()) {
        logger.warn('[ReviewService] Deliverable not found for resubmit', { reviewId });
        return;
      }
      const oldData = deliverableSnap.data();

      // Write review state to deliverable
      const automationNewData = {
        [`${prefix}Status`]: REVIEW_ITEM_STATUS.PENDING_REVIEW,
        [`${prefix}Complete`]: false
      };
      await updateDoc(deliverableRef, {
        ...automationNewData,
        automationPending: this._pendingAutomationMarker(oldData, automationNewData),
        updatedAt: serverTimestamp()
      });

      this._logDeliverableFieldChange(deliverableId, `${prefix}Status`, oldData[`${prefix}Status`], REVIEW_ITEM_STATUS.PENDING_REVIEW, 'Resubmitted for review');
      logger.info('[ReviewService] Resubmitted for review', { reviewId, statusType });

      // Items just went back to pending_review — clear any per-slot revision flags.
      await this._syncSlotChangeFlags(deliverableId, statusType, userId);

      // Trigger automation rules
      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const evaluateRules = httpsCallable(functions, 'evaluateDeliverableRules');
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: automationNewData,
        triggerType: 'field_change'
      }).then((result) => {
        this._clearPendingAutomation(deliverableRef);
        if (result.data?.rulesExecuted > 0) {
          logger.info('[ReviewService] Automation rules executed after resubmit', {
            deliverableId, rulesExecuted: result.data.rulesExecuted
          });
        }
      }).catch((err) => {
        // Marker deliberately left in place — retryPendingAutomation replays it.
        logger.warn('[ReviewService] Automation evaluation failed after resubmit', {
          deliverableId, error: err.message
        });
      });

    } catch (error) {
      logger.error('[ReviewService] Failed to resubmit for review', {
        error: error.message, reviewId, statusType
      });
      toastBridge.error('Failed to resubmit for review');
      throw error;
    }
  }

  // ─── Reset Review Status ─────────────────────────────────

  /**
   * Reset all item statuses for a given statusType back to pending_review
   * and clear the aggregate review state on the deliverable.
   * @param {string} reviewId
   * @param {string} statusType - 'internal' or 'client'
   * @param {string} userId
   */
  async resetReviewStatus(reviewId, statusType, userId) {
    try {
      const deliverableId = reviewId;
      const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';
      const prefix = statusType === 'internal' ? 'internalReview' : 'clientReview';

      // Batch-reset all items to pending_review
      const itemsRef = collection(db, 'reviews', reviewId, 'items');
      const itemsSnapshot = await getDocs(itemsRef);
      const batch = writeBatch(db);
      let resetCount = 0;

      itemsSnapshot.forEach((itemDoc) => {
        const itemStatus = itemDoc.data()[statusField];
        if (itemStatus && itemStatus !== REVIEW_ITEM_STATUS.PENDING_REVIEW) {
          resetCount++;
          const itemUpdates = {
            [statusField]: REVIEW_ITEM_STATUS.PENDING_REVIEW,
            updatedAt: serverTimestamp(),
            updatedBy: userId
          };
          // Clear approval fields
          if (statusType === 'internal') {
            itemUpdates.internalApprovedBy = null;
            itemUpdates.internalApprovedAt = null;
          } else {
            itemUpdates.clientApprovedBy = null;
            itemUpdates.clientApprovedAt = null;
          }
          itemUpdates.changesRequestedAtVersion = null;
          batch.update(itemDoc.ref, itemUpdates);
        }
      });

      await batch.commit();

      // Read current deliverable as oldData for automation diff
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      if (!deliverableSnap.exists()) {
        logger.warn('[ReviewService] Deliverable not found for review reset', { reviewId });
        return;
      }
      const oldData = deliverableSnap.data();

      // Clear aggregate review state on the deliverable
      const resetFields = {
        [`${prefix}Status`]: null,
        [`${prefix}StatusAt`]: null,
        [`${prefix}StatusBy`]: null,
        [`${prefix}Complete`]: false,
        updatedAt: serverTimestamp()
      };
      await updateDoc(deliverableRef, resetFields);

      deliverableService.logActivity(
        reviewId,
        'review_status_reset',
        `${prefix}Status`,
        oldData[`${prefix}Status`] ?? null,
        null,
        `${statusType === 'internal' ? 'Internal' : 'Client'} review reset — ${resetCount} item${resetCount === 1 ? '' : 's'}`
      ).catch(() => {});

      logger.info('[ReviewService] Review status reset', { reviewId, statusType });

      await this._syncSlotChangeFlags(deliverableId, statusType, userId);

      // Trigger automation rules
      const automationNewData = {
        [`${prefix}Status`]: null,
        [`${prefix}Complete`]: false
      };
      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const evaluateRules = httpsCallable(functions, 'evaluateDeliverableRules');
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: automationNewData,
        triggerType: 'field_change'
      }).catch((err) => {
        logger.warn('[ReviewService] Automation evaluation failed after review reset', {
          deliverableId, error: err.message
        });
      });

      toastBridge.success(`${statusType === 'internal' ? 'Internal' : 'Client'} review status reset`);
    } catch (error) {
      logger.error('[ReviewService] Failed to reset review status', {
        error: error.message, reviewId, statusType
      });
      toastBridge.error('Failed to reset review status');
      throw error;
    }
  }

  /**
   * Set all items to a specific review status (approved, changes_requested, pending_review).
   * For pending_review, delegates to resetReviewStatus.
   */
  async setBulkReviewStatus(reviewId, statusType, newStatus, userId, options = {}) {
    if (newStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW) {
      return this.resetReviewStatus(reviewId, statusType, userId);
    }
    const skipRuleIds = options.skipRuleIds || [];

    try {
      const statusField = statusType === 'internal' ? 'internalStatus' : 'clientStatus';
      const prefix = statusType === 'internal' ? 'internalReview' : 'clientReview';
      const deliverableId = reviewId;

      // Batch-update all items
      const itemsRef = collection(db, 'reviews', reviewId, 'items');
      const itemsSnapshot = await getDocs(itemsRef);
      const batch = writeBatch(db);

      itemsSnapshot.forEach((itemDoc) => {
        const itemUpdates = {
          [statusField]: newStatus,
          updatedAt: serverTimestamp(),
          updatedBy: userId
        };

        if (newStatus === REVIEW_ITEM_STATUS.APPROVED) {
          const approvedByField = statusType === 'internal' ? 'internalApprovedBy' : 'clientApprovedBy';
          const approvedAtField = statusType === 'internal' ? 'internalApprovedAt' : 'clientApprovedAt';
          itemUpdates[approvedByField] = userId;
          itemUpdates[approvedAtField] = serverTimestamp();
          itemUpdates.changesRequestedAtVersion = null;
        } else if (newStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
          const approvedByField = statusType === 'internal' ? 'internalApprovedBy' : 'clientApprovedBy';
          const approvedAtField = statusType === 'internal' ? 'internalApprovedAt' : 'clientApprovedAt';
          itemUpdates[approvedByField] = null;
          itemUpdates[approvedAtField] = null;
          itemUpdates.changesRequestedAtVersion = itemDoc.data().currentVersionNumber || 1;
          // Cascade: client changes_requested resets internal approval
          // so the item must pass internal review again before going back to client
          if (statusType === 'client') {
            itemUpdates.internalStatus = REVIEW_ITEM_STATUS.IN_REVISION;
            itemUpdates.internalApprovedBy = null;
            itemUpdates.internalApprovedAt = null;
          }
        }

        batch.update(itemDoc.ref, itemUpdates);
      });

      await batch.commit();

      // Read current deliverable as oldData for automation diff
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      const oldData = deliverableSnap.exists() ? deliverableSnap.data() : {};

      // Update aggregate review state on the deliverable
      const aggregateFields = {
        [`${prefix}Status`]: newStatus,
        [`${prefix}StatusAt`]: serverTimestamp(),
        [`${prefix}StatusBy`]: userId,
        [`${prefix}Complete`]: newStatus === REVIEW_ITEM_STATUS.APPROVED,
        updatedAt: serverTimestamp()
      };
      // Cascade: also reset internal review on deliverable when client requests changes
      if (statusType === 'client' && newStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
        aggregateFields.internalReviewStatus = REVIEW_ITEM_STATUS.IN_REVISION;
        aggregateFields.internalReviewComplete = false;
      }
      await updateDoc(deliverableRef, aggregateFields);

      deliverableService.logActivity(
        reviewId,
        'review_bulk_status_set',
        `${prefix}Status`,
        oldData[`${prefix}Status`] ?? null,
        newStatus,
        `${itemsSnapshot.size} items`
      ).catch(() => {});

      logger.info('[ReviewService] Bulk review status set', { reviewId, statusType, newStatus });

      await this._syncSlotChangeFlags(deliverableId, statusType, userId);

      // Trigger automation rules
      const automationNewData = {
        [`${prefix}Status`]: newStatus,
        [`${prefix}Complete`]: newStatus === REVIEW_ITEM_STATUS.APPROVED
      };
      if (statusType === 'client' && newStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
        automationNewData.internalReviewStatus = REVIEW_ITEM_STATUS.IN_REVISION;
        automationNewData.internalReviewComplete = false;
      }
      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const callableName = skipRuleIds.length > 0 ? 'evaluateDeliverableRulesFiltered' : 'evaluateDeliverableRules';
      const evaluateRules = httpsCallable(functions, callableName);
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: automationNewData,
        triggerType: 'field_change',
        ...(skipRuleIds.length > 0 ? { skipRuleIds } : {})
      }).catch((err) => {
        logger.warn('[ReviewService] Automation evaluation failed after bulk status set', {
          deliverableId, error: err.message
        });
      });

      const statusLabels = {
        [REVIEW_ITEM_STATUS.APPROVED]: 'Approved',
        [REVIEW_ITEM_STATUS.CHANGES_REQUESTED]: 'Changes Requested',
        [REVIEW_ITEM_STATUS.IN_REVISION]: 'In Revision'
      };
      toastBridge.success(`${statusType === 'internal' ? 'Internal' : 'Client'} review set to ${statusLabels[newStatus] || newStatus}`);
    } catch (error) {
      logger.error('[ReviewService] Failed to set bulk review status', {
        error: error.message, reviewId, statusType, newStatus
      });
      toastBridge.error('Failed to update review status');
      throw error;
    }
  }

  // ─── Presence ─────────────────────────────────────────────

  /**
   * Join a review as a viewer. Sets a viewer doc and starts a heartbeat interval.
   */
  async joinReview(reviewId) {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userName = await this._getUserName(user);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};

      const viewerRef = doc(db, 'reviews', reviewId, 'viewers', user.uid);
      const viewerData = {
        userId: user.uid,
        name: userName,
        email: user.email,
        avatar: userData.profile?.avatar || userData.avatar || null,
        userType: userData.systemRole || userData.userType || 'user',
        joinedAt: Timestamp.now(),
        lastSeen: Timestamp.now()
      };

      await setDoc(viewerRef, viewerData);

      // Clear any existing heartbeat for this review
      this._clearHeartbeat(reviewId);

      // Start heartbeat every 15 seconds
      const intervalId = setInterval(() => {
        this.heartbeat(reviewId);
      }, 15000);
      this._heartbeatIntervals.set(reviewId, intervalId);

      logger.info('[ReviewService] Joined review', { reviewId, userId: user.uid });
    } catch (error) {
      logger.error('[ReviewService] Failed to join review', { error: error.message, reviewId });
    }
  }

  /**
   * Leave a review — deletes the viewer doc and stops heartbeat.
   */
  async leaveReview(reviewId) {
    try {
      this._clearHeartbeat(reviewId);

      const user = auth.currentUser;
      if (!user) return;

      const viewerRef = doc(db, 'reviews', reviewId, 'viewers', user.uid);
      await deleteDoc(viewerRef);

      logger.info('[ReviewService] Left review', { reviewId, userId: user.uid });
    } catch (error) {
      logger.warn('[ReviewService] Failed to leave review', { error: error.message, reviewId });
    }
  }

  /**
   * Update lastSeen timestamp on the viewer doc.
   */
  async heartbeat(reviewId) {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const viewerRef = doc(db, 'reviews', reviewId, 'viewers', user.uid);
      await updateDoc(viewerRef, { lastSeen: Timestamp.now() });
    } catch {
      // Silently fail — viewer doc may have been cleaned up
    }
  }

  /**
   * Subscribe to the viewers subcollection. Filters out stale viewers (>2 min).
   */
  subscribeToViewers(reviewId, callback) {
    const key = `viewers_${reviewId}`;
    this.unsubscribe(key);

    const viewersRef = collection(db, 'reviews', reviewId, 'viewers');
    const unsub = onSnapshot(viewersRef, (snapshot) => {
      const now = Date.now();
      const staleThreshold = 45 * 1000; // 45 seconds
      const viewers = [];

      snapshot.forEach((docSnap) => {
        const data = { id: docSnap.id, ...docSnap.data() };
        const lastSeen = data.lastSeen?.toMillis?.() || data.lastSeen?.seconds * 1000 || 0;
        if (lastSeen === 0 || now - lastSeen < staleThreshold) {
          viewers.push(data);
        }
      });

      callback(viewers);
    }, (error) => {
      // Viewers is ephemeral presence data (45s stale window), not review content.
      // A transient listener error here is non-critical, so log at warn to keep it out
      // of the error log — unlike the review/items/comments subscriptions, which stay error.
      logger.warn('[ReviewService] Viewers subscription error', { error: error.message, reviewId });
    });

    this.listeners.set(key, unsub);
    return unsub;
  }

  /**
   * Clear heartbeat interval for a review.
   */
  _clearHeartbeat(reviewId) {
    if (this._heartbeatIntervals.has(reviewId)) {
      clearInterval(this._heartbeatIntervals.get(reviewId));
      this._heartbeatIntervals.delete(reviewId);
    }
  }

  // ─── Cleanup ───────────────────────────────────────────────

  /**
   * Unsubscribe a specific listener by key
   */
  unsubscribe(key) {
    if (this.listeners.has(key)) {
      this.listeners.get(key)();
      this.listeners.delete(key);
    }
  }

  /**
   * Unsubscribe all listeners
   */
  unsubscribeAll() {
    this.listeners.forEach((unsub, key) => {
      logger.info('[ReviewService] Unsubscribing', { key });
      unsub();
    });
    this.listeners.clear();

    // Clear all heartbeat intervals
    this._heartbeatIntervals.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this._heartbeatIntervals.clear();
  }

  // ─── Private Helpers ───────────────────────────────────────

  /**
   * Sync in_revision status to the deliverable when client requests changes.
   * This ensures the deliverable's internalReviewStatus reflects in_revision
   * so automation rules with "was in_revision" triggers can fire later.
   */
  async _syncInternalRevisionToDeliverable(reviewId, userId) {
    try {
      const deliverableId = reviewId;
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      if (!deliverableSnap.exists()) return;

      const oldData = deliverableSnap.data();

      const revisionFields = {
        internalReviewStatus: REVIEW_ITEM_STATUS.IN_REVISION,
        internalReviewStatusAt: serverTimestamp(),
        internalReviewStatusBy: userId,
        internalReviewComplete: false,
        updatedAt: serverTimestamp()
      };
      await updateDoc(deliverableRef, revisionFields);
      this._logDeliverableFieldChange(deliverableId, 'internalReviewStatus', oldData.internalReviewStatus, REVIEW_ITEM_STATUS.IN_REVISION, 'Client requested changes');

      logger.info('[ReviewService] Synced in_revision to deliverable', { reviewId });

      // Trigger automation rules
      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const evaluateRules = httpsCallable(functions, 'evaluateDeliverableRules');
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: {
          internalReviewStatus: REVIEW_ITEM_STATUS.IN_REVISION,
          internalReviewComplete: false
        },
        triggerType: 'field_change'
      }).catch((err) => {
        logger.warn('[ReviewService] Automation evaluation failed after in_revision sync', {
          deliverableId, error: err.message
        });
      });
    } catch (error) {
      logger.warn('[ReviewService] Failed to sync in_revision to deliverable', {
        error: error.message, reviewId
      });
    }
  }

  /**
   * Sync finalization state from review items to the deliverable document.
   * Follows the same pattern as syncReviewStateToDeliverable.
   */
  async _syncFinalizationToDeliverable(reviewId, userId) {
    try {
      const deliverableId = reviewId;
      const itemsRef = collection(db, 'reviews', reviewId, 'items');
      const itemsSnapshot = await getDocs(itemsRef);

      let hasFinalization = false;
      let hasPending = false;
      let allComplete = true;

      itemsSnapshot.forEach((itemDoc) => {
        const data = itemDoc.data();
        if (data.requiresFinalization) {
          hasFinalization = true;
          if (data.finalizationStatus === FINALIZATION_STATUS.PENDING) {
            hasPending = true;
            allComplete = false;
          } else if (data.finalizationStatus !== FINALIZATION_STATUS.COMPLETE) {
            allComplete = false;
          }
        }
      });

      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      if (!deliverableSnap.exists()) return;

      const oldData = deliverableSnap.data();

      let finalizationFields;
      if (!hasFinalization) {
        finalizationFields = {
          finalizationStatus: null,
          finalizationStatusAt: null,
          finalizationStatusBy: null,
          finalizationComplete: false
        };
      } else if (hasPending) {
        finalizationFields = {
          finalizationStatus: FINALIZATION_STATUS.PENDING,
          finalizationStatusAt: serverTimestamp(),
          finalizationStatusBy: userId,
          finalizationComplete: false
        };
      } else if (allComplete) {
        finalizationFields = {
          finalizationStatus: FINALIZATION_STATUS.COMPLETE,
          finalizationStatusAt: serverTimestamp(),
          finalizationStatusBy: userId,
          finalizationComplete: true
        };
      } else {
        finalizationFields = {
          finalizationStatus: null,
          finalizationStatusAt: serverTimestamp(),
          finalizationStatusBy: userId,
          finalizationComplete: false
        };
      }

      await updateDoc(deliverableRef, { ...finalizationFields, updatedAt: serverTimestamp() });
      this._logDeliverableFieldChange(deliverableId, 'finalizationStatus', oldData.finalizationStatus, finalizationFields.finalizationStatus, 'Synced from review');
      logger.info('[ReviewService] Synced finalization state to deliverable', {
        reviewId, deliverableId, finalizationStatus: finalizationFields.finalizationStatus
      });

      // Trigger automation rules
      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const evaluateRules = httpsCallable(functions, 'evaluateDeliverableRules');
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: {
          finalizationStatus: finalizationFields.finalizationStatus,
          finalizationComplete: finalizationFields.finalizationComplete
        },
        triggerType: 'field_change'
      }).catch((err) => {
        logger.warn('[ReviewService] Automation evaluation failed after finalization sync', {
          deliverableId, error: err.message
        });
      });
    } catch (error) {
      logger.warn('[ReviewService] Failed to sync finalization to deliverable', {
        error: error.message, reviewId
      });
    }
  }

  /**
   * Reset review-related fields on the deliverable document.
   * Called when items are added or all items are removed, since
   * the previous aggregate review state is no longer valid.
   */
  async _resetDeliverableReviewState(reviewId, reason = null) {
    try {
      const deliverableId = reviewId;
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      if (!deliverableSnap.exists()) return;

      const oldData = deliverableSnap.data();

      const resetFields = {
        internalReviewStatus: null,
        internalReviewStatusAt: null,
        internalReviewStatusBy: null,
        internalReviewComplete: false,
        clientReviewStatus: null,
        clientReviewStatusAt: null,
        clientReviewStatusBy: null,
        clientReviewComplete: false,
        clientReviewDecided: false,
        clientReviewDecidedAt: null,
        clientReviewDecidedBy: null,
        finalizationStatus: null,
        finalizationStatusAt: null,
        finalizationStatusBy: null,
        finalizationComplete: false,
        updatedAt: serverTimestamp()
      };

      await updateDoc(deliverableRef, resetFields);
      if (
        oldData.internalReviewStatus != null || oldData.clientReviewStatus != null ||
        oldData.finalizationStatus != null || !!oldData.clientReviewDecided
      ) {
        deliverableService.logActivity(
          deliverableId,
          'review_state_reset',
          null,
          null,
          null,
          reason ? `Review state reset — ${reason}` : 'Review state reset'
        ).catch(() => {});
      }
      logger.info('[ReviewService] Deliverable review state reset', { reviewId });

      // Trigger automation rules with the diff
      const sanitizedOldData = this._sanitizeForCallable(oldData);
      const evaluateRules = httpsCallable(functions, 'evaluateDeliverableRules');
      evaluateRules({
        deliverableId,
        oldData: sanitizedOldData,
        newData: {
          internalReviewStatus: null,
          internalReviewComplete: false,
          clientReviewStatus: null,
          clientReviewComplete: false,
          finalizationStatus: null,
          finalizationComplete: false
        },
        triggerType: 'field_change'
      }).catch((err) => {
        logger.warn('[ReviewService] Automation evaluation failed after review reset', {
          deliverableId, error: err.message
        });
      });
    } catch (error) {
      logger.warn('[ReviewService] Failed to reset deliverable review state', {
        error: error.message, reviewId
      });
    }
  }

  async _getUserName(user) {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.name) return data.name;
        if (data.firstName && data.lastName) return `${data.firstName} ${data.lastName}`;
        if (data.firstName) return data.firstName;
      }
    } catch {
      // Fallback
    }
    return user.displayName || user.email;
  }

  async _getItemName(reviewId, itemId) {
    try {
      const snap = await getDoc(doc(db, 'reviews', reviewId, 'items', itemId));
      if (snap.exists()) return snap.data().name || 'item';
    } catch {
      // Fallback
    }
    return 'item';
  }

  /**
   * Map a status transition (statusType + new status) to an activity log entry.
   * Skips no-op transitions and cascade-only transitions (in_revision, etc.)
   * which are already implied by the human action that triggered them.
   */
  async _logStatusChange(reviewId, itemBefore, statusType, newStatus) {
    if (!itemBefore) return;
    const oldStatus = statusType === 'internal'
      ? itemBefore.internalStatus
      : itemBefore.clientStatus;
    if (oldStatus === newStatus) return;

    const itemName = itemBefore.name || 'item';
    const versionLabel = `v${itemBefore.currentVersionNumber || 1}`;
    const ITEM = `"${itemName}" ${versionLabel}`;

    let action = null;
    let note = null;

    if (statusType === 'internal') {
      if (newStatus === REVIEW_ITEM_STATUS.APPROVED) {
        action = 'review_internal_approved';
        note = `Approved ${ITEM} internally`;
      } else if (newStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
        action = 'review_internal_changes_requested';
        note = `Requested changes on ${ITEM}`;
      } else if (
        newStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW &&
        oldStatus === REVIEW_ITEM_STATUS.APPROVED
      ) {
        action = 'review_internal_unapproved';
        note = `Reverted internal approval on ${ITEM}`;
      } else if (newStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW) {
        action = 'review_internal_reopened';
        note = `Reopened ${ITEM} for internal review`;
      }
    } else if (statusType === 'client') {
      if (newStatus === REVIEW_ITEM_STATUS.APPROVED) {
        action = 'review_client_approved';
        note = `Client approved ${ITEM}`;
      } else if (newStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) {
        action = 'review_client_changes_requested';
        note = `Client requested changes on ${ITEM}`;
      } else if (newStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW) {
        action = 'review_client_unapproved';
        note = `Reverted client decision on ${ITEM}`;
      }
    }

    if (!action) return;

    const field = statusType === 'internal' ? 'internalStatus' : 'clientStatus';
    await deliverableService.logActivity(reviewId, action, field, oldStatus, newStatus, note);
  }

  /**
   * Log a deliverable field write only when the value actually changes.
   * Fire-and-forget; logActivity never throws.
   */
  _logDeliverableFieldChange(deliverableId, field, oldValue, newValue, note) {
    if ((oldValue ?? null) === (newValue ?? null)) return;
    deliverableService.logActivity(deliverableId, 'field_update', field, oldValue ?? null, newValue ?? null, note);
  }

  /**
   * Log the user-visible item field changes made through updateItem.
   */
  _logItemUpdates(reviewId, before, updates, keys) {
    const itemName = before.name || 'item';
    for (const key of keys) {
      const oldValue = before[key] ?? null;
      const newValue = updates[key] ?? null;
      if (oldValue === newValue) continue;
      if (key === 'name') {
        deliverableService.logActivity(reviewId, 'review_item_renamed', 'name', oldValue, newValue,
          `"${oldValue || 'Untitled'}" → "${newValue || 'Untitled'}"`);
      } else if (key === 'requiresFinalization') {
        deliverableService.logActivity(reviewId, 'review_finalization_required_changed', key, !!oldValue, !!newValue,
          `Finalization ${newValue ? 'on' : 'off'} for "${itemName}"`);
      } else if (key === 'finalizationNote') {
        deliverableService.logActivity(reviewId, 'review_finalization_note_updated', key, oldValue, newValue,
          `Finalization note updated for "${itemName}"`);
      } else {
        deliverableService.logActivity(reviewId, 'review_status_overridden', key, oldValue, newValue,
          `Status override on "${itemName}"`);
      }
    }
  }

  _sanitizeForCallable(data) {
    const result = {};
    for (const [key, value] of Object.entries(data)) {
      if (value && typeof value.toDate === 'function') {
        result[key] = value.toDate().toISOString();
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  _reviewFileType(file) {
    const assetType = detectAssetType(file);
    if (['videos', 'selects', 'social'].includes(assetType)) return 'video';
    if (['photography', 'graphics'].includes(assetType)) return 'image';
    if (assetType === 'audio') return 'audio';
    return 'document';
  }
}

// Export singleton instance
export const reviewService = new ReviewService();
